import { useQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import type { TourAudienceCriteria } from "./types";
import { LOCATION_OPTIONS } from "./types";
import { TOUR_MILESTONE_KEYS } from "@shared/tours/milestone-keys";

interface RoleSlugsResponse {
  slugs: Array<{ slug: string; label: string }>;
}

interface VisibilityEditorProps {
  value: TourAudienceCriteria;
  onChange: (next: TourAudienceCriteria) => void;
  showRoles?: boolean;
  showOnboarding?: boolean;
  showLocations?: boolean;
  showMilestones?: boolean;
}

export function VisibilityEditor({
  value,
  onChange,
  showRoles = true,
  showOnboarding = true,
  showLocations = true,
  showMilestones = true,
}: VisibilityEditorProps) {
  const rolesQuery = useQuery<RoleSlugsResponse>({
    queryKey: ["/api/admin/tours/role-slugs"],
    enabled: showRoles,
  });

  const allRoles = rolesQuery.data?.slugs ?? [];
  const selectedRoles = value.roles ?? [];
  const labelBySlug = new Map(allRoles.map((r) => [r.slug, r.label]));
  const selectedLocations = value.locations ?? [];
  const milestoneHas = value.milestones?.has ?? [];
  const milestoneLacks = value.milestones?.lacks ?? [];

  function toggleRole(slug: string) {
    const next = selectedRoles.includes(slug)
      ? selectedRoles.filter((r) => r !== slug)
      : [...selectedRoles, slug];
    onChange({ ...value, roles: next.length ? next : undefined });
  }

  function toggleLocation(code: string) {
    const next = selectedLocations.includes(code)
      ? selectedLocations.filter((l) => l !== code)
      : [...selectedLocations, code];
    onChange({ ...value, locations: next.length ? next : undefined });
  }

  function toggleMilestoneHas(key: string) {
    const next = milestoneHas.includes(key)
      ? milestoneHas.filter((k) => k !== key)
      : [...milestoneHas, key];
    const milestones = {
      ...value.milestones,
      has: next.length ? next : undefined,
    };
    onChange({
      ...value,
      milestones: milestones.has || milestones.lacks ? milestones : undefined,
    });
  }

  function toggleMilestoneLacks(key: string) {
    const next = milestoneLacks.includes(key)
      ? milestoneLacks.filter((k) => k !== key)
      : [...milestoneLacks, key];
    const milestones = {
      ...value.milestones,
      lacks: next.length ? next : undefined,
    };
    onChange({
      ...value,
      milestones: milestones.has || milestones.lacks ? milestones : undefined,
    });
  }

  return (
    <div className="space-y-4">
      {showRoles && (
        <div className="space-y-2">
          <Label>Roles</Label>
          <p className="text-xs text-muted-foreground">
            Leave empty to include all roles in the audience.
          </p>
          <div className="flex flex-wrap gap-2">
            {allRoles.map((role) => {
              const on = selectedRoles.includes(role.slug);
              return (
                <button
                  key={role.slug}
                  type="button"
                  onClick={() => toggleRole(role.slug)}
                  data-testid={`chip-role-${role.slug}`}
                >
                  <Badge
                    variant={on ? "default" : "outline"}
                    className="cursor-pointer"
                  >
                    {role.label}
                    {on && <X className="w-3 h-3 ml-1" />}
                  </Badge>
                </button>
              );
            })}
            {selectedRoles
              .filter((slug) => !labelBySlug.has(slug))
              .map((slug) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleRole(slug)}
                  data-testid={`chip-role-${slug}`}
                >
                  <Badge variant="default" className="cursor-pointer">
                    {slug}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                </button>
              ))}
            {allRoles.length === 0 && (
              <p className="text-xs text-muted-foreground">No roles available.</p>
            )}
          </div>
        </div>
      )}

      {showOnboarding && (
        <div className="space-y-2">
          <Label htmlFor="vis-onboarding">Onboarding state</Label>
          <Select
            value={value.onboardingState ?? "any"}
            onValueChange={(v) =>
              onChange({
                ...value,
                onboardingState: v === "any" ? undefined : (v as "new" | "completed"),
              })
            }
          >
            <SelectTrigger id="vis-onboarding" data-testid="select-onboarding">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="new">New users only</SelectItem>
              <SelectItem value="completed">Completed users only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showLocations && (
        <div className="space-y-2">
          <Label>Locations</Label>
          <p className="text-xs text-muted-foreground">
            Leave empty to include all locations.
          </p>
          <div className="flex flex-wrap gap-2">
            {LOCATION_OPTIONS.map((loc) => {
              const on = selectedLocations.includes(loc.code);
              return (
                <button
                  key={loc.code}
                  type="button"
                  onClick={() => toggleLocation(loc.code)}
                  data-testid={`chip-location-${loc.code}`}
                >
                  <Badge variant={on ? "default" : "outline"} className="cursor-pointer">
                    {loc.label}
                    {on && <X className="w-3 h-3 ml-1" />}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showMilestones && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Must have milestone</Label>
            <p className="text-xs text-muted-foreground">
              User must have all selected milestones to see this step/tour.
            </p>
            <div className="flex flex-wrap gap-2">
              {TOUR_MILESTONE_KEYS.map((key) => {
                const on = milestoneHas.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleMilestoneHas(key)}
                    data-testid={`chip-milestone-has-${key}`}
                  >
                    <Badge variant={on ? "default" : "outline"} className="cursor-pointer font-mono text-[11px]">
                      {key}
                      {on && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Must lack milestone</Label>
            <p className="text-xs text-muted-foreground">
              User must NOT have any selected milestones to see this step/tour.
            </p>
            <div className="flex flex-wrap gap-2">
              {TOUR_MILESTONE_KEYS.map((key) => {
                const on = milestoneLacks.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleMilestoneLacks(key)}
                    data-testid={`chip-milestone-lacks-${key}`}
                  >
                    <Badge variant={on ? "default" : "outline"} className="cursor-pointer font-mono text-[11px]">
                      {key}
                      {on && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
